import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { LoadingSpinner } from '@/shared/components/feedback'
import { ProtectedRoute, PublicOnlyRoute } from '@/shared/components/auth/ProtectedRoute'

// Lazy load pages for code splitting
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/RegisterPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))
const TermsPage = lazy(() => import('@/pages/TermsPage'))
const DataDeletionPage = lazy(() => import('@/pages/DataDeletionPage'))

// Dashboard pages
const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'))
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'))
const ChannelsPage = lazy(() => import('@/pages/dashboard/ChannelsPage'))
const ChannelNewPage = lazy(() => import('@/pages/dashboard/ChannelNewPage'))
const ChannelDetailPage = lazy(() => import('@/pages/dashboard/ChannelDetailPage'))
const VideosPage = lazy(() => import('@/pages/dashboard/VideosPage'))
const VideoDetailPage = lazy(() => import('@/pages/dashboard/VideoDetailPage'))
const VideoProcessingPage = lazy(() => import('@/pages/dashboard/VideoProcessingPage'))
const ContentManagerPage = lazy(() => import('@/pages/dashboard/ContentManagerPage'))
const YouTubePage = lazy(() => import('@/pages/dashboard/YouTubePage'))
const YouTubeCallbackPage = lazy(() => import('@/pages/dashboard/YouTubeCallbackPage'))
const MetaCallbackPage = lazy(() => import('@/pages/dashboard/MetaCallbackPage'))
const InstagramCallbackPage = lazy(() => import('@/pages/dashboard/InstagramCallbackPage'))
const MetaPage = lazy(() => import('@/pages/dashboard/MetaPage'))
const WebhookPage = lazy(() => import('@/pages/dashboard/WebhookPage'))
const AnalyticsPage = lazy(() => import('@/pages/dashboard/AnalyticsPage'))
const SettingsProcessingPage = lazy(() => import('@/pages/dashboard/SettingsProcessingPage'))
const SettingsAutomationPage = lazy(() => import('@/pages/dashboard/SettingsAutomationPage'))
const ProfilePage = lazy(() => import('@/pages/dashboard/ProfilePage'))
const GuidePage = lazy(() => import('@/pages/GuidePage'))
const UploadHistoryPage = lazy(() => import('@/pages/dashboard/UploadHistoryPage'))
const NotificationsPage = lazy(() => import('@/pages/dashboard/NotificationsPage'))

// Admin pages
const AdminLayout = lazy(() => import('@/layouts/AdminLayout'))
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'))
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'))
const AdminPlansPage = lazy(() => import('@/pages/admin/AdminPlansPage'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'))
const AdminLogsPage = lazy(() => import('@/pages/admin/AdminLogsPage'))
const AdminCredentialsPage = lazy(() => import('@/pages/admin/AdminCredentialsPage'))

function SuspenseWrapper() {
    return (
        <Suspense
            fallback={
                <div className="min-h-screen animated-gradient flex items-center justify-center">
                    <LoadingSpinner size="lg" />
                </div>
            }
        >
            <Outlet />
        </Suspense>
    )
}

export default function App() {
    return (
        <Routes>
            <Route element={<SuspenseWrapper />}>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
                <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/data-deletion" element={<DataDeletionPage />} />

                {/* Dashboard routes (auth-protected) */}
                <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                    <Route index element={<DashboardPage />} />
                    <Route path="channels" element={<ChannelsPage />} />
                    <Route path="channels/new" element={<ChannelNewPage />} />
                    <Route path="channels/:id" element={<ChannelDetailPage />} />
                    <Route path="videos" element={<VideosPage />} />
                    <Route path="videos/:id" element={<VideoDetailPage />} />
                    <Route path="videos/processing" element={<VideoProcessingPage />} />
                    <Route path="content" element={<ContentManagerPage />} />
                    <Route path="youtube/*" element={<YouTubePage />} />
                    <Route path="youtube/callback" element={<YouTubeCallbackPage />} />
                    <Route path="meta/callback" element={<MetaCallbackPage />} />
                    <Route path="instagram/callback" element={<InstagramCallbackPage />} />
                    <Route path="meta/*" element={<MetaPage />} />
                    <Route path="webhook" element={<WebhookPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="settings/processing" element={<SettingsProcessingPage />} />
                    <Route path="settings/automation" element={<SettingsAutomationPage />} />
                    <Route path="settings" element={<Navigate to="/dashboard/youtube" replace />} />
                    <Route path="settings/youtube" element={<Navigate to="/dashboard/youtube" replace />} />
                    <Route path="settings/meta" element={<Navigate to="/dashboard/meta" replace />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="guide" element={<GuidePage />} />
                    <Route path="guide/youtube-api" element={<Navigate to="/dashboard/youtube" replace />} />
                    <Route path="guide/tiktok-api" element={<Navigate to="/dashboard/channels" replace />} />
                    <Route path="guide/facebook-api" element={<Navigate to="/dashboard/meta" replace />} />
                    <Route path="guide/x-api" element={<Navigate to="/dashboard" replace />} />
                    <Route path="uploads" element={<UploadHistoryPage />} />
                    <Route path="notifications" element={<NotificationsPage />} />
                </Route>

                {/* Admin routes (admin-protected) */}
                <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}>
                    <Route index element={<AdminPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="plans" element={<AdminPlansPage />} />
                    <Route path="logs" element={<AdminLogsPage />} />
                    <Route path="credentials" element={<AdminCredentialsPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                </Route>
            </Route>
        </Routes>
    )
}
